// src/pages/account/ProfilePage.jsx
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "../../utils/apparifySwal";
import { CalendarDays } from "lucide-react";

import { useTheme } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import { userAPI } from "../../services/profileService";
import { getImageUrl } from "../../services/api";

import { useAccountSwal } from "../../features/account/useAccountSwal";
import { useProfile } from "../../features/account/useProfile";
import { useReveal } from "../../features/account/useReveal";
import { FIELD_CONFIG } from "../../features/account/profileConfig";
import { clean } from "../../features/account/profileValidators";
import { maskField } from "../../features/account/masking";

import SectionCard from "../../components/account/SectionCard";
import AccountField from "../../components/account/AccountField";
import AvatarCard from "../../components/account/AvatarCard";
import BirthDatePickerModal, { formatBirthDateLabel } from "../../components/BirthDatePickerModal";
import { FormSkeleton } from "../../components/skeletons/ShopperSkeletons";

/* ─── helpers ─── */

/**
 * Format an ISO date string (YYYY-MM-DD) as a full human-readable date.
 * e.g. "1998-07-15" → "July 15, 1998"
 * Appending T00:00:00Z forces UTC interpretation so no timezone shift occurs.
 */
function formatDate(isoStr) {
  if (!isoStr) return isoStr;
  const d = new Date(isoStr.length === 10 ? `${isoStr}T00:00:00Z` : isoStr);
  if (isNaN(d.getTime())) return isoStr;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * Extract the raw avatar value from any API response shape and resolve it
 * to a proper absolute URL using getImageUrl().
 * Falls back gracefully to the provided fallback string.
 */
function pickAvatarUrl(payload, fallback = "") {
  const u = payload?.user || payload?.data?.user || payload?.data || payload || null;
  const raw =
    u?.avatarUrl || u?.avatar || u?.url ||
    payload?.avatarUrl || payload?.avatar || payload?.url ||
    null;
  return raw ? getImageUrl(raw) : fallback;
}

/* ─── component ─── */

export default function ProfilePage() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { logout, setUser } = useAuth();
  const { swalBase, toast } = useAccountSwal();

  const fileRef = useRef(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [birthdatePickerOpen, setBirthdatePickerOpen] = useState(false);

  const {
    loading,
    initial,
    form,
    errors,
    editingKey,
    savingField,
    setField,
    beginEdit,
    cancelEdit,
    saveField,
    onKeyDownMaybeSave,
    setForm,
    setInitial,
    savedTick,
  } = useProfile({
    swalBase,
    toast,
    onAuthExpired: () => {
      logout();
      navigate("/login", { replace: true });
    },
  });

  const { revealed, revealing, isRevealed, triggerReveal, revokeReveal } =
    useReveal({ swalBase });

  const fullName =
    form.firstName || form.lastName
      ? `${clean(form.firstName)} ${clean(form.lastName)}`.trim()
      : "Your Account";

  const pickAvatar = () => fileRef.current?.click();

  // Sync initial profile into auth context
  useEffect(() => {
    if (!loading && initial) {
      setUser((prev) => ({
        ...(prev && typeof prev === "object" ? prev : {}),
        ...initial,
      }));
    }
  }, [loading, initial, setUser]);

  // Sync updates to auth context when fields are saved
  useEffect(() => {
    if (!savedTick || Object.keys(savedTick).length === 0) return;
    setUser((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const updates = {};
      for (const k of Object.keys(savedTick)) {
        if (form[k] !== undefined) updates[k] = form[k];
      }
      return Object.keys(updates).length ? { ...base, ...updates } : base;
    });
  }, [savedTick, form, setUser]);

  const onAvatarFile = async (file) => {
    if (!file) return;
    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "UNSUPPORTED FILE", html: "Please use JPG, PNG, or WEBP.", confirmButtonText: "OK" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      await Swal.fire({ ...swalBase, icon: "warning", title: "FILE TOO LARGE", html: "Max size is 5 MB.", confirmButtonText: "OK" });
      return;
    }

    const preview = URL.createObjectURL(file);
    setForm((p) => ({ ...p, avatarUrl: preview }));
    setUploadingAvatar(true);

    try {
      const res = await userAPI.uploadAvatar(file);
      const nextUrl = pickAvatarUrl(res, preview);
      setForm((p) => ({ ...p, avatarUrl: nextUrl }));
      setInitial((p) => ({ ...p, avatarUrl: nextUrl }));
      setUser?.((prev) => ({ ...(prev && typeof prev === "object" ? prev : {}), avatarUrl: nextUrl, avatar: nextUrl }));
      await toast.fire({ icon: "success", title: "AVATAR UPDATED" });
    } catch (e) {
      setForm((p) => ({ ...p, avatarUrl: initial.avatarUrl || "" }));
      await Swal.fire({ ...swalBase, icon: "error", title: "AVATAR UPLOAD FAILED", html: e?.message || "Avatar upload failed.", confirmButtonText: "OK" });
    } finally {
      setUploadingAvatar(false);
      try { URL.revokeObjectURL(preview); } catch {}
    }
  };

  /* ─── render a single editable field row ─── */
  const renderEditableField = (key, baseHelper = "") => {
    const cfg = FIELD_CONFIG[key] || {};
    const canEdit = cfg.editable !== false;
    const isEditing = editingKey === key && canEdit;
    const lockedByOther = editingKey && editingKey !== key;
    const saving = !!savingField[key];
    const sensitive = !!cfg.sensitive;

    let displayValue = form[key] ?? "";
    let inputType = cfg.type || "text";
    let helperText = baseHelper;

    if (sensitive) {
      if (isEditing) {
        // Input always starts empty for sensitive fields (shoulder-surf protection).
        // displayValue is "" because beginEdit() clears it.
        displayValue = form[key] ?? "";
        if (key === "birthdate") inputType = "date";

        // Show the current masked value as a hint so the user knows what they're replacing.
        const maskedHint = maskField(key, initial?.[key] ?? "");
        if (maskedHint) {
          helperText = `Current: ${maskedHint}${helperText ? ` · ${helperText}` : ""}`;
        }
      } else if (isRevealed && revealed?.[key]) {
        // Step-up auth completed — show the real value temporarily.
        // Birthdate is formatted as "July 15, 1998" for readability.
        displayValue =
          key === "birthdate" ? formatDate(revealed[key]) : revealed[key];
        inputType = "text";
      } else {
        // Default: always show masked value.
        // The server already returns masked values; maskField is a safety net.
        displayValue = maskField(key, form[key] ?? "");
        inputType = "text";
      }
    } else {
      // Non-sensitive fields: select options, etc.
      if (cfg.type === "select" && cfg.options) {
        inputType = "select";
        return (
          <AccountField
            key={key}
            label={cfg.label}
            value={displayValue || ""}
            onChange={(e) => setField(key, e.target.value)}
            onKeyDown={(e) => isEditing && onKeyDownMaybeSave(key, e)}
            placeholder={cfg.placeholder}
            type="select"
            options={cfg.options}
            inputMode={cfg.inputMode}
            maxLength={cfg.maxLen}
            error={errors[key]}
            helper={helperText}
            isDark={isDark}
            readOnly={!isEditing}
            disabled={lockedByOther || !canEdit}
            actionLabel={isEditing ? "SAVE" : canEdit ? "EDIT" : undefined}
            onAction={() => (isEditing ? saveField(key) : canEdit && beginEdit(key))}
            actionDisabled={lockedByOther || (!isEditing && !canEdit)}
            actionLoading={saving}
            secondaryActionLabel={isEditing ? "CANCEL" : null}
            onSecondaryAction={() => cancelEdit(key)}
            secondaryActionDisabled={saving}
          />
        );
      }
    }

    const isBirthdateEditor = key === "birthdate" && isEditing;

    const handlePrimaryAction = () => {
      if (isEditing) {
        saveField(key);
        return;
      }

      if (!canEdit) return;
      if (key === "birthdate" && !lockedByOther) {
        beginEdit(key);
        setBirthdatePickerOpen(true);
        return;
      }

      beginEdit(key);
    };

    return (
      <AccountField
        key={key}
        label={cfg.label}
        value={isBirthdateEditor ? formatBirthDateLabel(form.birthdate) : displayValue || ""}
        onChange={(e) => setField(key, e.target.value)}
        onKeyDown={(e) => isEditing && onKeyDownMaybeSave(key, e)}
        placeholder={isEditing ? cfg.placeholder : ""}
        type={inputType}
        inputMode={cfg.inputMode}
        maxLength={cfg.maxLen}
        error={errors[key]}
        helper={helperText}
        isDark={isDark}
        readOnly={!isEditing}
        disabled={lockedByOther || !canEdit}
        actionLabel={isEditing ? "SAVE" : canEdit ? "EDIT" : undefined}
        onAction={handlePrimaryAction}
        actionDisabled={lockedByOther || (!isEditing && !canEdit)}
        actionLoading={saving}
        secondaryActionLabel={isEditing ? "CANCEL" : null}
        onSecondaryAction={() => {
          setBirthdatePickerOpen(false);
          cancelEdit(key);
        }}
        secondaryActionDisabled={saving}
        onControlClick={isBirthdateEditor ? () => setBirthdatePickerOpen(true) : undefined}
        controlIcon={isBirthdateEditor ? <CalendarDays className="h-4 w-4 shrink-0 text-neutral-400 dark:text-neutral-500" aria-hidden="true" /> : null}
        controlAriaHaspopup={isBirthdateEditor ? "dialog" : undefined}
        controlAriaExpanded={isBirthdateEditor ? birthdatePickerOpen : undefined}
      />
    );
  };

  /* ─── reveal toggle button ─── */
  const RevealToggle = () => (
    <button
      type="button"
      onClick={isRevealed ? revokeReveal : triggerReveal}
      disabled={revealing}
      title={
        isRevealed
          ? "Hide your email, phone, and birthdate"
          : "Reveal your email, phone, and birthdate (requires password)"
      }
      className={[
        "flex items-center gap-1.5 rounded-xl px-2.5 py-1.5",
        "text-[10px] font-semibold tracking-[0.18em] transition",
        "disabled:opacity-50",
        isRevealed
          ? "bg-amber-50 text-amber-700 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50"
          : "border border-neutral-200 text-neutral-500 hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-400 dark:hover:bg-neutral-800",
      ].join(" ")}
    >
      {revealing ? (
        <>
          <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
          </svg>
          VERIFYING…
        </>
      ) : isRevealed ? (
        <>
          {/* eye-slash icon */}
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M3.28 2.22a.75.75 0 00-1.06 1.06l14.5 14.5a.75.75 0 101.06-1.06l-1.745-1.745a10.029 10.029 0 003.3-4.38 1.651 1.651 0 000-1.185A10.004 10.004 0 009.999 3a9.956 9.956 0 00-4.744 1.194L3.28 2.22zM7.752 6.69l1.092 1.092a2.5 2.5 0 013.374 3.373l1.091 1.092a4 4 0 00-5.557-5.557z" clipRule="evenodd" />
            <path d="M10.748 13.93l2.523 2.524a9.987 9.987 0 01-3.27.547c-4.258 0-7.894-2.66-9.337-6.41a1.651 1.651 0 010-1.186A10.007 10.007 0 012.839 6.02L6.07 9.252a4 4 0 004.678 4.678z" />
          </svg>
          HIDE
        </>
      ) : (
        <>
          {/* eye icon */}
          <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path d="M10 12.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />
            <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 010-1.186A10.004 10.004 0 0110 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0110 17c-4.257 0-7.893-2.66-9.336-6.41zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
          REVEAL
        </>
      )}
    </button>
  );

  /* ─── loading skeleton ─── */
  if (loading) {
    return <FormSkeleton label="Loading account profile" />;
  }

  return (
    <div className="space-y-4">
      <AvatarCard
        name={fullName}
        email={form.email}
        avatarUrl={form.avatarUrl}
        uploading={uploadingAvatar}
        onPickFile={pickAvatar}
        isDark={isDark}
      />

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] || null;
          e.target.value = "";
          onAvatarFile(file);
        }}
      />

      <SectionCard
        title="Account Information"
        subtitle="Edit one field at a time · Save per field · Enter to save · Esc to cancel"
        isDark={isDark}
        className="mb-2"
      >
        <div className="space-y-3">
          {renderEditableField("firstName")}
          {renderEditableField("lastName")}
          {renderEditableField("username")}
        </div>
      </SectionCard>

      <SectionCard
        title="Contact"
        subtitle={
          isRevealed
            ? "Details revealed · auto-hides in 5 min"
            : "Email, phone, and birthdate are always masked"
        }
        action={<RevealToggle />}
        isDark={isDark}
        className="mb-2"
      >
        <div className="space-y-3">
          {renderEditableField("email")}
          {renderEditableField("phone")}
          {renderEditableField("birthdate")}
          {renderEditableField("gender")}
        </div>
      </SectionCard>

      <BirthDatePickerModal
        open={birthdatePickerOpen}
        value={form.birthdate}
        onClose={() => setBirthdatePickerOpen(false)}
        onConfirm={(birthdate) => {
          setField("birthdate", birthdate);
          setBirthdatePickerOpen(false);
        }}
      />
    </div>
  );
}
